// List of reMarkable IdP hosts to prevent users from entering their own reMarkable sign-in URL instead of their customer IdP sign-in URL.
import {
  validateSamlCertificateContent,
  isBase64EncodedPemCertificateContent,
} from './samlCertificate';
export {
  extractSamlCertificateMetadata,
  type SamlCertificateMetadata,
} from './samlCertificate';

const REMARKABLE_IDP_HOSTS = new Set([
  'my.remarkable.com',
  'auth.remarkable.com',
]);

export const SAML_VALIDATION_MESSAGES = {
  signInUrlProtocolRequired:
    'Please enter the full sign-in URL starting with https://',
  signInUrlRemarkableHost:
    'This must be your customer IdP sign-in URL, not a reMarkable URL.',
  signInUrlInvalid: 'Please enter a valid URL.',
  certificateNonBase64:
    'The uploaded .pem/.cer file is not base64 encoded. Please upload a base64 encoded certificate.',
  certificateInvalid:
    'Please upload a valid base64 encoded PEM or CER certificate file.',
} as const;

const ALLOWED_CERTIFICATE_FILE_EXTENSIONS = ['.pem', '.cer'];

export type SamlCertificateValidationReason =
  | 'valid'
  | 'unsupported_extension'
  | 'non_base64_pem_or_cer'
  | 'invalid_certificate_content'
  | 'missing_certificate';

export interface SamlCertificateValidationResult {
  isValid: boolean;
  reason: SamlCertificateValidationReason;
  message?: string;
  fileName?: string;
}

export const validateSamlSignInUrl = (value: string) => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return false;

  const normalizedValue = trimmedValue.toLowerCase();
  const hasHttpProtocol = normalizedValue.startsWith('http://');
  const hasHttpsProtocol = normalizedValue.startsWith('https://');

  if (!hasHttpProtocol && !hasHttpsProtocol) {
    return SAML_VALIDATION_MESSAGES.signInUrlProtocolRequired;
  }

  try {
    const parsedUrl = new URL(trimmedValue);
    const hostname = parsedUrl.hostname.toLowerCase();

    if (REMARKABLE_IDP_HOSTS.has(hostname)) {
      return SAML_VALIDATION_MESSAGES.signInUrlRemarkableHost;
    }

    return !!hostname;
  } catch {
    return SAML_VALIDATION_MESSAGES.signInUrlInvalid;
  }
};

const hasAllowedCertificateExtension = (fileName: string) =>
  ALLOWED_CERTIFICATE_FILE_EXTENSIONS.some((extension) =>
    fileName.toLowerCase().endsWith(extension)
  );

export const getSamlCertificateValidationError = async (
  fileList?: FileList,
  fallbackCertificate?: string
) => {
  const validation = await inspectSamlCertificateValidation(
    fileList,
    fallbackCertificate
  );

  return validation.message;
};

export const inspectSamlCertificateValidation = async (
  fileList?: FileList,
  fallbackCertificate?: string
): Promise<SamlCertificateValidationResult> => {
  const file = fileList?.[0];

  if (file) {
    if (!hasAllowedCertificateExtension(file.name)) {
      return {
        isValid: false,
        reason: 'unsupported_extension',
        message: SAML_VALIDATION_MESSAGES.certificateInvalid,
        fileName: file.name,
      };
    }

    const content = await file.text();
    if (validateSamlCertificateContent(content)) {
      return {
        isValid: true,
        reason: 'valid',
        fileName: file.name,
      };
    }

    if (!isBase64EncodedPemCertificateContent(content)) {
      return {
        isValid: false,
        reason: 'non_base64_pem_or_cer',
        message: SAML_VALIDATION_MESSAGES.certificateNonBase64,
        fileName: file.name,
      };
    }

    return {
      isValid: false,
      reason: 'invalid_certificate_content',
      message: SAML_VALIDATION_MESSAGES.certificateInvalid,
      fileName: file.name,
    };
  }

  if (validateSamlCertificateContent(fallbackCertificate)) {
    return {
      isValid: true,
      reason: 'valid',
    };
  }

  return {
    isValid: false,
    reason: 'missing_certificate',
    message: SAML_VALIDATION_MESSAGES.certificateInvalid,
  };
};

export const validateSamlCertificateFile = async (
  fileList?: FileList,
  fallbackCertificate?: string
) => {
  const validation = await inspectSamlCertificateValidation(
    fileList,
    fallbackCertificate
  );

  return validation.isValid;
};
